import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import {
  ColorNames,
  ColorShades,
  TooltipOptions,
  Utils,
} from 'marketpro-design-lib';
import { createEmpty, extend, Extent } from 'ol/extent';
import Feature, { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import Geometry from 'ol/geom/Geometry';
import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import VectorImageLayer from 'ol/layer/VectorImage';
import { default as OlMap } from 'ol/Map';
import Overlay from 'ol/Overlay';
import * as olProj from 'ol/proj';
import RenderFeature from 'ol/render/Feature';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import View from 'ol/View';
import { combineLatest, forkJoin, of, Subject } from 'rxjs';
import { debounceTime, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { constants } from '../../../../constants';
import { AuthService } from '../../../auth/services/auth.service';
import { AuthorizedResourcesService } from '../../../auth/services/authorized-resources.service';
import {
  GroupedResponse,
  GroupedResponseTwoLevels,
} from '../../models/grouped-statistics-response.model';
import { RegionSelection } from '../../models/region-selection.model';
import { SimpleStatisticsResponse } from '../../models/simple-statistics-response.model';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { DataService } from '../../services/data.service';
import { SystemGroupsService } from '../../services/system-groups.service';
import { UpsaleModalService } from '../../services/upsale-modal.service';
import { continents } from './../../../../regions';
import { MapHeaderData } from './map-header/map-header.component';
import { HttpClient } from '@angular/common/http';
import { CitiesService } from '../../services/cities.service';
import { SelectorCity } from '../../models/region-selection-cities.model';
import { Region } from '../../models/region-model';
import Text from 'ol/style/Text';
import { DashboardStatisticsService } from '../../services/dashboard-statistics.service';
import { CitiesConsumerSpending } from '../../models/cities-data.model';
import { exhaustMapWithTrailing } from 'rxjs-exhaustmap-with-trailing';
import { JsonValues, LegendLabel } from '../../models/map.model';
import { CategoryService } from '../../services/categories.service';
import { StatisticsService } from '../../services/statistics.service';
import { SpendingGroup } from '../../models/spending-group.model';
import {
  ConsumerDataDetails,
  ContinentData,
} from '../../models/statistics.model';
import { Permissions } from '../../../auth/models/permissions.model';

const LEGEND_LENGTH: number = 6;
const COUNTRIES_OUTLINE_CLASS_NAME = 'countriesOutlines';

@Component({
  selector: 'mpro-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, OnDestroy, AfterViewInit {
  public isCategories: boolean = false;
  public map: OlMap;
  public worldMap: BaseLayer;
  public subnationalLayer?: BaseLayer;
  public citiesLayer?: BaseLayer;
  public showCountriesOutsideRegion: boolean = false;
  public lastSelectedCountriesSize: number;
  public expandLegend: boolean = false;
  public dataFilters: DataFilters = new DataFilters();
  public semiSelectedMode: string | undefined = undefined;
  public currentSelection: RegionSelection = new RegionSelection();
  public openToolSelection: boolean = false;
  public isLoading: boolean = false;
  @ViewChild('tooltip')
  public tooltip: ElementRef;
  public mapTooltip: TooltipOptions;
  public spendingGroupColor: ColorNames = ColorNames.Blue;
  public legendColors: Map<ColorNames, number[][]> = new Map([
    [
      ColorNames.Blue,
      Utils.interpolateColors(
        { name: ColorNames.Blue, shade: ColorShades.Shade100 },
        { name: ColorNames.Blue, shade: ColorShades.Shade600 },
        LEGEND_LENGTH
      ),
    ],
    [
      ColorNames.Red,
      Utils.interpolateColors(
        { name: ColorNames.Red, shade: ColorShades.Shade100 },
        { name: ColorNames.Red, shade: ColorShades.Shade600 },
        LEGEND_LENGTH
      ),
    ],
    [
      ColorNames.Apricot,
      Utils.interpolateColors(
        { name: ColorNames.Apricot, shade: ColorShades.Shade100 },
        { name: ColorNames.Apricot, shade: ColorShades.Shade600 },
        LEGEND_LENGTH
      ),
    ],
    [
      ColorNames.Green,
      Utils.interpolateColors(
        { name: ColorNames.Green, shade: ColorShades.Shade100 },
        { name: ColorNames.Green, shade: ColorShades.Shade600 },
        LEGEND_LENGTH
      ),
    ],
  ]);
  public legend: LegendLabel[] = [];
  private maxZoom = 19;

  private dataCacheByYearAndState: GroupedResponseTwoLevels<SimpleStatisticsResponse>;
  private current: GroupedResponse<SimpleStatisticsResponse>;
  private totalDataByYearAndState?: GroupedResponseTwoLevels<SimpleStatisticsResponse>; // data for the whole population with no filters applied
  private totalDataByYear?: GroupedResponse<SimpleStatisticsResponse>;
  private worldData: SimpleStatisticsResponse = new SimpleStatisticsResponse();
  private mapHeaderData: MapHeaderData;
  private popupOverlay: Overlay;
  private maxValue: number = 0;
  private minValue: number = 0;
  private mapData: { [key: string]: number } = {};
  private animationDuration: number = 800;
  private ngUnsubscribe: Subject<void> = new Subject();
  private jsonValues: JsonValues;
  private getConsumerSpending$ = new Subject<
    | {
        startYear: number;
        endYear: number;
        cityName: string;
        coordinates: number[];
      }
    | undefined
  >();
  private lastHoveredCity = '';
  private lastHoveredCityData: ConsumerDataDetails & {
    cityName: string;
  };
  private maxTextDisplayResolution = 20000;
  public permissions: Permissions;

  constructor(
    public dataService: DataService,
    public authService: AuthService,
    private citiesService: CitiesService,
    private upsaleModalService: UpsaleModalService,
    private systemGroupsService: SystemGroupsService,
    private dataFiltersService: DataFiltersService,
    private authorizedResources: AuthorizedResourcesService,
    private dashboardService: DashboardStatisticsService,
    private gtmService: GoogleTagManagerService,
    private http: HttpClient,
    private categoryService: CategoryService,
    private statisticsService: StatisticsService
  ) {
    this.resetTooltip();

    const jsonLoaders = forkJoin({
      features: this.http.get(constants.uriParts.features),
      continentsFeatures: this.http.get(constants.uriParts.continentsFeatures),
      extent: this.http.get(constants.uriParts.extent),
      subnationalFeatures: this.http.get(
        constants.uriParts.subnationalFeatures
      ),
    });

    jsonLoaders.subscribe((json) => {
      this.jsonValues = {
        continentsFeatures: json.continentsFeatures,
        extent: json.extent,
        features: json.features,
        subnationalFeatures: json.subnationalFeatures,
      };

      this.initMap();
    });
  }

  public ngOnInit(): void {
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .pipe(tap(() => (this.isLoading = true)))
      .subscribe((filters) => {
        this.dataFilters = filters;
        // update selection if not in semiSelectedMode + when reset event(regions - 0, countries - 0, subnational - 0) is triggered
        if (
          !this.semiSelectedMode ||
          (!this.dataFilters.regionSelection.regions.size &&
            !this.dataFilters.regionSelection.countries.size &&
            !this.dataFilters.regionSelection.subnational.size)
        ) {
          this.currentSelection = this.dataFilters.regionSelection;
        }
        if (!this.dataFilters.regionSelection.countries.size) {
          this.removeCitiesLayer();
        }

        if (
          this.lastSelectedCountriesSize < 2 &&
          this.currentSelection.countries.size > 1
        ) {
          this.showCountriesOutsideRegion = false;
        } else if (this.currentSelection.countries.size < 2) {
          this.showCountriesOutsideRegion = true;
        }

        if (!this.authService.isLoggedIn()) {
          if (this.dataFilters.regionSelection.regions.size === 1) {
            this.openToolSelection = true;
          } else {
            this.openToolSelection = false;
          }
        }

        if (this.map) {
          this.handleSelectionChanges();
        }
        this.statisticsService.getContinents$.next();
        this.lastSelectedCountriesSize =
          this.dataFilters.regionSelection.countries.size;
      });
    this.authService.categoriesEnabled$.subscribe(
      (isCategories) => (this.isCategories = isCategories)
    );
    this.dataService
      .getData()
      .pipe(takeUntil(this.ngUnsubscribe), debounceTime(300))
      .subscribe((data) => {
        this.spendingGroupColor =
          this.dataFilters.spendingGroup instanceof SpendingGroup
            ? this.systemGroupsService.getSpendingGroupColor(
                this.dataFilters.spendingGroup.greaterThan,
                this.dataFilters.spendingGroup.lessThan
              ).name
            : ColorNames.Green;

        if (data.countryData.current && data.countryData.total) {
          this.totalDataByYearAndState = data.countryData.total;
          this.totalDataByYear = data.consumerSpendingData.total;
          this.dataCacheByYearAndState = data.countryData.current;
        }

        this.updateLegendColors();
        this.updateLegend();
        this.updateMap();
        this.isLoading = false;
      });

    this.citiesService.citiesMapList$.subscribe((cities) => {
      if (cities?.length) {
        this.removeCitiesLayer();
        this.addCitiesLayer(cities);
      } else {
        this.removeCitiesLayer();
      }
    });
    this.getCitiyConsumerSpending();

    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((permissions) => {
        this.permissions = permissions;
        if (permissions.isFreemium) {
          this.getCountriesData();
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getCountriesData() {
    this.statisticsService.getContinents$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(300),
        switchMap(() => {
          this.isLoading = true;
          return this.statisticsService
            .getContinentsData()
            .pipe(tap(() => (this.isLoading = false)));
        })
      )
      .subscribe((response) => {
        const res =
          this.dataFilters.spendingGroup instanceof SpendingGroup
            ? response.spendingDetails
            : response.incomeDetails;
        this.spendingGroupColor =
          this.dataFilters.spendingGroup instanceof SpendingGroup
            ? this.systemGroupsService.getSpendingGroupColor(
                this.dataFilters.spendingGroup.greaterThan,
                this.dataFilters.spendingGroup.lessThan
              ).name
            : ColorNames.Green;
        this.updateLegendColors();
        this.maxValue = Math.max(...res.map((continent) => continent.amount));
        this.minValue = Math.min(...res.map((continent) => continent.amount));
        this.current = res.reduce(
          (prev, cur) => ({
            ...prev,
            [cur.code]: {
              headCount: cur.population,
              spending2021ppp: cur.amount,
              spendingCurrentUsd: cur.amount,
            },
          }),
          {}
        );

        this.setStatisticsWorldData(res);
        this.calculateData(this.current);
        this.updateStatisticsLegend();
        this.worldMap?.changed();
        this.subnationalLayer?.changed();
        this.citiesLayer?.changed();
      });
  }

  private setStatisticsWorldData(world: ContinentData[]) {
    this.worldData = new SimpleStatisticsResponse();
    world.forEach((continent) => {
      this.worldData.spendingCurrentUsd += continent.amount;
      this.worldData.spending2021ppp += continent.amountPerCapita;
      this.worldData.spendingCurrentLcu += continent.amountSharePercentage;
      this.worldData.headCount += continent.population;
    });
  }

  private updateStatisticsLegend() {
    // Reset legend value, given that the size can change inside of the map
    this.legend = Array(LEGEND_LENGTH).fill({
      legendLabel: '',
      min: 0,
      max: 0,
    });
    const interval = Math.sqrt(this.maxValue) / this.legend.length;
    this.legend = this.legend.reduce((accumulator: LegendLabel[], _, i) => {
      let min: number = i * interval;
      let max: number = min + interval;

      const isLastLegendItem = i === this.legend.length - 1;
      let legendLabel: string;
      min = min ** 2;
      max = max ** 2;

      legendLabel = `$${Utils.formatNumber(min, true, '.0-0')}${
        isLastLegendItem ? '+' : '-'
      }${isLastLegendItem ? '' : Utils.formatNumber(max, true, '.0-0')}`;
      if (Math.ceil(max) > this.minValue) {
        accumulator.push({
          min,
          max: isLastLegendItem ? undefined : max,
          legendLabel,
        });
      }

      return accumulator;
    }, []);

    // Since `this.legend` can change its length, we now have to update the amount of items in the legend accordingly
    this.updateLegendColors();
  }

  public ngAfterViewInit(): void {}

  private removeCitiesLayer() {
    this.map
      ?.getLayers()
      ?.getArray()
      .filter((layer) => layer.getClassName() === 'CitiesLayer')
      .forEach((layer) => this.map.removeLayer(layer));
  }
  private getText(feature, currentResolution) {
    let text = feature.get('CITY');
    if (currentResolution > this.maxTextDisplayResolution) {
      text = '';
    }
    return text;
  }
  private createTextStyle(feature, currentResolution) {
    return new Text({
      text: this.getText(feature, currentResolution),
      stroke: new Stroke({
        color: '#fff',
        width: 4,
      }),
      font: '12px sans-serif',
      fill: new Fill({ color: '#000' }),
      placement: 'point',
      overflow: true,
      textBaseline: 'bottom',
      offsetY: -5,
    });
  }
  private lineStyleFunction(feature, currentResolution) {
    return new Style({
      fill: new Fill({
        color: 'rgba(0, 0, 255, 0)',
      }),
      text: this.createTextStyle(feature, currentResolution),
    });
  }
  public addCitiesLayer(cities: SelectorCity[]) {
    const features = {
      type: 'FeatureCollection',
      features: cities.map((city) => ({
        type: 'Feature',
        properties: {
          CITY: city.name,
          CITY_POPULATION: city.headCount,
        },
        geometry: {
          type: 'Polygon',
          coordinates: city.boundary && [city.boundary.map((r) => [r.x, r.y])],
        },
      })),
    };
    let vectorSource = new VectorSource({
      features: new GeoJSON().readFeatures(features, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      }),
    });

    this.citiesLayer = new VectorImageLayer({
      imageRatio: 2,
      source: vectorSource,
      zIndex: 2,
      className: 'CitiesLayer',
      style: (f, resolution) => this.lineStyleFunction(f, resolution),
    });
    this.map.addLayer(this.citiesLayer);
    this.updateMap();
  }

  public getCssColor(c: number[]) {
    return `rgb(${c[0]},${c[1]},${c[2]})`;
  }

  protected reset(): void {
    this.map.getView().animate({
      center: olProj.fromLonLat([0, 18]),
      zoom: 2,
      duration: this.animationDuration,
    });
  }

  public backToWorldwideView() {
    this.currentSelection.clearAll();
    this.dataFiltersService.patchDataFilters({
      regionSelection: this.currentSelection,
    });
  }

  public onMapHeaderChanged(mapHeaderData: MapHeaderData) {
    this.mapHeaderData = mapHeaderData;
    if (this.worldMap) {
      this.updateLegend();
      this.updateMap();
    }
  }

  public onShowCountriesOutsideRegionChange(state: boolean) {
    this.showCountriesOutsideRegion = state;
    if (!this.showCountriesOutsideRegion && !this.dataFilters.dataLens) {
      this.setWorldData(this.getCodes());
    } else if (this.dataFilters.dataLens) {
      this.setWorldData(Object.keys(this.current));
    } else {
      this.resetWorldData();
    }

    this.updateLegend();
    this.updateMap();
  }

  public backToSelectedRegion() {
    this.semiSelectedMode = undefined;
    this.dataFiltersService.patchDataFilters({
      regionSelection: this.currentSelection,
    });
  }

  private getCodes(): string[] {
    if (this.authService.displayContinets()) {
      return this.currentSelection.getRegionCodes();
    }

    return this.dataFilters.dataLens
      ? this.currentSelection.getSubnationalCodes()
      : this.currentSelection.getCountryCodes();
  }

  private getWorldData(countryCode: string): SimpleStatisticsResponse {
    if (
      this.mapHeaderData.type === 'share_of_country' &&
      this.totalDataByYearAndState
    ) {
      const selectedYearTotals = <GroupedResponse<SimpleStatisticsResponse>>(
        this.totalDataByYearAndState[this.dataFilters.year]
      );

      return selectedYearTotals[countryCode];
    }

    return this.worldData;
  }

  private getDailySpendingPerCapita(
    spending: number,
    headCount: number
  ): number {
    return spending / Utils.getDaysInAYear(this.dataFilters.year) / headCount;
  }

  private getAnnualSpendingPerCapita(
    spending: number,
    headCount: number
  ): number {
    return spending / headCount;
  }

  private getCitiyConsumerSpending() {
    this.getConsumerSpending$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        exhaustMapWithTrailing((props) => {
          if (!props) {
            return of(undefined);
          }
          this.popupOverlay.setPosition(props.coordinates);
          if (this.lastHoveredCity === props.cityName) {
            return of(this.lastHoveredCityData);
          }
          return this.dashboardService.searchCities(props.cityName).pipe(
            map((cities) => {
              this.mapTooltip.loading = true;
              const city = cities.find(
                (city) => city.cityId === props.cityName
              );
              return {
                startYear: props.startYear,
                endYear: props.endYear,
                continentCode: city?.continentCode,
                countryCode: city?.countryCode,
                cityName: props.cityName,
              };
            }),
            switchMap(
              ({
                startYear,
                endYear,
                cityName,
                continentCode,
                countryCode,
              }) => {
                if (!continentCode || !countryCode) {
                  return of(undefined);
                }
                return this.statisticsService
                  .getConsumerData({
                    startYear,
                    endYear,
                    countryCode,
                    cityName,
                  })
                  .pipe(
                    map((response) => {
                      const res =
                        this.dataFilters.spendingGroup instanceof SpendingGroup
                          ? response.spendingData
                          : response.incomeData;
                      this.lastHoveredCity = cityName;
                      this.lastHoveredCityData = {
                        ...res,
                        cityName: cityName,
                      };
                      return {
                        ...res,
                        cityName: cityName,
                      };
                    })
                  );
              }
            )
          );
        })
      )
      .subscribe((cityData) => {
        cityData && this.setCitiesTooltipData(cityData);
      });
  }

  private updateLegend(): void {
    this.dataCacheByYearAndState &&
      this.handleData(this.dataCacheByYearAndState[constants.initialYear]);
    this.calculateData(this.current);

    // Reset legend value, given that the size can change inside of the map
    this.legend = Array(LEGEND_LENGTH).fill({
      legendLabel: '',
      min: 0,
      max: 0,
    });
    const isShareOfCountry: boolean =
      this.mapHeaderData.type === 'share_of_country';
    // if isShareOfCountry calculate min, max normally - share of country should work with the old formula
    // otherwise
    // Add SQRT of the maxValue as the biggest interval, to improve the distribution
    const interval =
      (isShareOfCountry ? this.maxValue : Math.sqrt(this.maxValue)) /
      this.legend.length;

    // Changed the `map` to a reduce to be able to filter the legend when there would be empty blocs
    this.legend = this.legend.reduce((accumulator: LegendLabel[], _, i) => {
      let min: number = i * interval;
      let max: number = min + interval;

      const isLastLegendItem = i === this.legend.length - 1;
      let legendLabel: string;
      if (isShareOfCountry) {
        legendLabel = `${Utils.formatNumber(
          min,
          false,
          '.0-0'
        )}-${Utils.formatNumber(max, false, '.0-0')}%`;
      } else {
        // Use the sqrt values to compare with the interval, and then convert back to the regular values
        min = min ** 2;
        max = max ** 2;

        legendLabel = `${Utils.formatNumber(min, true, '.0-0')}${
          isLastLegendItem ? '+' : '-'
        }${isLastLegendItem ? '' : Utils.formatNumber(max, true, '.0-0')}`;
        if (this.mapHeaderData?.title !== 'people') {
          legendLabel = `$${legendLabel}`;
        } else {
          legendLabel = `${legendLabel}`;
        }
      }
      // Checks if the current `max` value is bigger than the lowest figure of the set. If false it means we would have an empty category, so now that will be skipped.
      if (Math.ceil(max) > this.minValue || isShareOfCountry) {
        accumulator.push({
          min,
          max: isLastLegendItem ? undefined : max,
          legendLabel,
        });
      }

      return accumulator;
    }, []);

    // Since `this.legend` can change its length, we now have to update the amount of items in the legend accordingly
    this.updateLegendColors();
  }

  private updateLegendColors(): void {
    // Updates the length of `this.legendColors` according to the length of `this.legend`
    this.legendColors.set(
      this.spendingGroupColor,
      Utils.interpolateColors(
        { name: this.spendingGroupColor, shade: ColorShades.Shade100 },
        { name: this.spendingGroupColor, shade: ColorShades.Shade600 },
        this.legend.length
      )
    );
  }

  private setWorldData(world: string[]) {
    this.worldData = new SimpleStatisticsResponse();
    world.forEach((countryCode) => {
      this.worldData.spendingCurrentUsd +=
        this.current[countryCode]?.spendingCurrentUsd || 0;
      this.worldData.spendingOtherPersonalCare +=
        this.current[countryCode]?.spendingOtherPersonalCare || 0;
      this.worldData.spendingBeauty +=
        this.current[countryCode]?.spendingBeauty || 0;
      this.worldData.spendingAllPersonalCare +=
        this.current[countryCode]?.spendingAllPersonalCare || 0;
      this.worldData.spendingHairCare +=
        this.current[countryCode]?.spendingHairCare || 0;
      this.worldData.spendingHygiene +=
        this.current[countryCode]?.spendingHygiene || 0;
      this.worldData.spendingFoodAndBeverages +=
        this.current[countryCode]?.spendingFoodAndBeverages || 0;
      this.worldData.spendingSkinCare +=
        this.current[countryCode]?.spendingSkinCare || 0;
      this.worldData.spendingFragrances +=
        this.current[countryCode]?.spendingFragrances || 0;
      this.worldData.spendingMakeup +=
        this.current[countryCode]?.spendingMakeup || 0;
      this.worldData.spendingBeautyPPP +=
        this.current[countryCode]?.spendingBeautyPPP || 0;
      this.worldData.spendingPersonalCarePPP +=
        this.current[countryCode]?.spendingPersonalCarePPP || 0;
      this.worldData.spendingHairCarePPP +=
        this.current[countryCode]?.spendingHairCarePPP || 0;
      this.worldData.spendingHygienePPP +=
        this.current[countryCode]?.spendingHygienePPP || 0;
      this.worldData.spendingSkinCarePPP +=
        this.current[countryCode]?.spendingSkinCarePPP || 0;
      this.worldData.spendingFragrancesPPP +=
        this.current[countryCode]?.spendingFragrancesPPP || 0;
      this.worldData.spendingMakeupPPP +=
        this.current[countryCode]?.spendingMakeupPPP || 0;
      this.worldData.spending2021ppp +=
        this.current[countryCode]?.spending2021ppp || 0;
      this.worldData.spendingCurrentLcu +=
        this.current[countryCode]?.spendingCurrentLcu || 0;
      this.worldData.headCount += this.current[countryCode]?.headCount || 0;
    });
  }

  private resetWorldData() {
    if (this.totalDataByYear) {
      this.worldData = this.totalDataByYear[this.dataFilters.year];
    }
  }

  private initMap() {
    let vectorSource = new VectorSource({
      features: new GeoJSON().readFeatures(
        this.permissions.isFreemium
          ? this.jsonValues.continentsFeatures
          : this.jsonValues.features,
        {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        }
      ),
    });

    this.worldMap = new VectorImageLayer({
      imageRatio: 2,
      source: vectorSource,
      style: (f, resolution: number) =>
        this.colorMapArea(
          resolution,
          <Feature<Geometry>>f,
          this.getCodes(),
          this.showCountriesOutsideRegion
        ),
    });

    this.map = new OlMap({
      target: 'map',
      layers: [this.worldMap],
      view: new View({
        center: olProj.fromLonLat([0, 18]),
        zoom: 2,
        minZoom: 2,
        maxZoom: this.maxZoom,
      }),
    });

    this.map.getView().on('change:resolution', () => {
      this.map.render();
    });

    if (!this.authService.isLoggedIn()) {
      const countries = new VectorSource({
        features: new GeoJSON().readFeatures(this.jsonValues.features, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        }),
      });

      const countriesVector = new VectorImageLayer({
        className: COUNTRIES_OUTLINE_CLASS_NAME,
        imageRatio: 2,
        source: countries,
        style: (f, resolution: number) =>
          this.styleCountriesLayer(resolution, <Feature<Geometry>>f),
      });

      this.map.addLayer(countriesVector);
    }

    // creating a new layer that contains the country border selection is
    // much faster (performance wise) compared to changing the style of the country
    // on the countries layer
    var featureOverlay = new VectorLayer({
      source: new VectorSource(),
      map: this.map,
      style: () => {
        return new Style({
          stroke: new Stroke({
            color: Utils.getColorHex({
              name: this.spendingGroupColor,
              shade: ColorShades.Shade700,
            }),
            width: 2,
          }),
        });
      },
    });

    this.popupOverlay = new Overlay({
      element: this.tooltip.nativeElement,
      offset: [9, 9],
    });
    this.map.addOverlay(this.popupOverlay);

    let currentHover: Feature<Geometry> | null = null;
    let selected: FeatureLike | null = null;
    this.map.on('pointermove', (event) => {
      if (currentHover !== null) {
        featureOverlay?.getSource()?.removeFeature(currentHover);
        currentHover = null;
      }

      if (event.dragging) {
        return false;
      }

      if (!this.map.getFeaturesAtPixel(event.pixel).length) {
        this.tooltip.nativeElement.hidden = true;
        return false;
      }
      this.map.forEachFeatureAtPixel(event.pixel, (f, layer) => {
        if (selected !== null) {
          (selected as any).setStyle(undefined);
          selected = null;
        }
        let displayCities = false;
        if (f.get('CITY')) {
          this.getConsumerSpending$.next({
            cityName: f.get('CITY'),
            endYear: this.authorizedResources.endYear,
            startYear: this.authorizedResources.startYear,
            coordinates: event.coordinate,
          });
          selected = f;
          const selectStyle = new Style({
            stroke: new Stroke({
              color: Utils.getColorHex({
                name: this.spendingGroupColor,
                shade: ColorShades.Shade700,
              }),
              width: 1,
            }),
            fill: new Fill({
              color: '',
            }),
            text: this.createTextStyle(f, this.map.getView().getResolution()),
          });
          (selectStyle as any).getFill().setColor(
            Utils.getColorHex({
              name: this.spendingGroupColor,
              shade: ColorShades.Shade300,
            })
          );
          (f as any).setStyle(selectStyle);
          displayCities = true;
          return true;
        }
        if (displayCities) {
          this.getConsumerSpending$.next(undefined);
          displayCities = false;
        }
        const areaCode = f.get('adm0_a3') || f.get('NUTS_ID') || f.get('CODE'); // either country code or subnational code or continet code
        const countryCode = f.get('CNTR_CODE'); // only for subnational areas
        const openToolCountry =
          layer &&
          layer.getClassName() === COUNTRIES_OUTLINE_CLASS_NAME &&
          [...this.currentSelection.regions.values()][0]?.countries.some(
            (code) => code === areaCode
          );
        if (
          (!this.current || !this.current[areaCode]) &&
          !openToolCountry &&
          !f.get('CODE')
        ) {
          this.tooltip.nativeElement.hidden = true;
          return true;
        }

        currentHover = <Feature<Geometry>>f;
        featureOverlay?.getSource()?.addFeature(currentHover);
        this.setTooltipData(f, openToolCountry);
        this.popupOverlay.setPosition(event.coordinate);

        return true;
      });

      return false;
    });

    var self = this;
    this.map.getViewport().addEventListener(
      'mouseout',
      function (evt) {
        self.tooltip.nativeElement.hidden = true;
      },
      false
    );

    this.map.on('click', (event) => {
      if (!this.map.getFeaturesAtPixel(event.pixel).length) {
        this.tooltip.nativeElement.hidden = true;
        return;
      }
      //Check for top layer (cities)
      if (this.map.getFeaturesAtPixel(event.pixel).length === 1) {
        this.map.forEachFeatureAtPixel(event.pixel, (f, layer) => {
          const areaCode =
            f.get('adm0_a3') || f.get('NUTS_ID') || f.get('CODE'); // either country code or subnational code
          const countryCode = f.get('CNTR_CODE'); // only for subnational areas
          const openToolCountry =
            layer &&
            layer.getClassName() === COUNTRIES_OUTLINE_CLASS_NAME &&
            [...this.currentSelection.regions.values()][0]?.countries.some(
              (code) => code === areaCode
            );
          if (openToolCountry) {
            this.upsaleModalService.open();
            return true;
          }
          const name =
            f.get('name') ||
            f.get('NUTS_NAME') ||
            f.get('CONTINENT') ||
            f.get('CITY');
          let currentSelection = this.currentSelection;
          if (!this.getCodes().find((selected) => selected === areaCode)) {
            if (this.dataFilters.dataLens) {
              currentSelection.subnational.clear();
            } else {
              currentSelection.clearAll();
            }
          }
          if (this.current[areaCode]) {
            const gtmTag = {
              event: 'mapClick',
              areaCode: areaCode,
              areaName: name,
              areaAction: this.getCodes().find(
                (selected) => selected === areaCode
              )
                ? 'Deselect'
                : 'Select',
            };
            this.gtmService.pushTag(gtmTag);
            if (this.dataFilters.dataLens) {
              currentSelection.toggleProvince({ name: name, code: areaCode });
            } else {
              const selectedCountry = this.authorizedResources.countries.find(
                (country) => country.code === areaCode
              );
              if (selectedCountry) {
                if (this.semiSelectedMode !== areaCode) {
                  if (
                    currentSelection.countries.size > 1 &&
                    this.getCodes().find((selected) => selected === areaCode)
                  ) {
                    currentSelection = new RegionSelection();
                    this.semiSelectedMode = areaCode;
                  }
                  selectedCountry.region = continents.find(
                    (continent) => continent.name === f.get('continent')
                  )?.code;
                  if (!f.get('CITY')) {
                    const region = continents.find(
                      (continent) =>
                        continent.name.toLocaleLowerCase() ===
                        f.getProperties().continent.toLocaleLowerCase()
                    );
                    if (!region) {
                      return;
                    }
                    this.citiesService.getCountries$.next({
                      continent: region as Region,
                    });
                    if (currentSelection.countries.has(selectedCountry.code)) {
                      this.citiesService.getCities$.next(undefined);
                    } else {
                      this.citiesService.getCities$.next({
                        continentCode: region.code,
                        countryCode: selectedCountry.code,
                      });
                    }
                  }
                  currentSelection.toggleCountry(selectedCountry);
                } else {
                  this.semiSelectedMode = undefined;
                }
              } else if (f.get('CODE')) {
                const continent = continents.find(
                  (continent) => continent.code === f.get('CODE')
                );
                if (continent) {
                  if (!this.authService.displayContinets) {
                    currentSelection.toggleRegion(
                      continent,
                      this.authorizedResources.countriesMap
                    );
                  } else {
                    currentSelection.setFreemiumRegion(continent);
                  }
                }
              }
            }
            // TODO: create emitter in map component and move patchDataFilters to the HeaderComponent ?
            this.dataFiltersService.patchDataFilters({
              regionSelection: currentSelection,
            });

            return true;
          }

          return false;
        });
      } else if (this.citiesLayer) {
        this.map.forEachFeatureAtPixel(event.pixel, (f) => {
          if (!f.get('CITY')) {
            return true;
          }
          const currentSelection = this.currentSelection;
          currentSelection.toggleCity({ name: f.get('CITY') });
          this.dataFiltersService.patchDataFilters({
            regionSelection: currentSelection,
          });
          return true;
        });
      }
    });
  }

  private setCitiesTooltipData(
    city: ConsumerDataDetails & { cityName: string }
  ) {
    this.resetTooltip();
    (this.mapTooltip.title = city.cityName.toLocaleUpperCase()),
      (this.mapTooltip.spending = city.amount),
      (this.mapTooltip.people = city.population),
      (this.mapTooltip.dailySpending = city.perCapitaAmount);
    this.tooltip.nativeElement.hidden = false;
    this.mapTooltip.loading = false;
  }

  private setTooltipData(
    f: RenderFeature | Feature<Geometry>,
    openToolCountryTooltip: boolean = false
  ) {
    this.resetTooltip();

    const code = f.get('adm0_a3') || f.get('CODE') || f.get('NUTS_ID');
    const name = f.get('admin') || f.get('CONTINENT') || f.get('NUTS_NAME');

    if (openToolCountryTooltip) {
      this.mapTooltip.title = name.toUpperCase();
      this.mapTooltip.showLock = true;
      this.tooltip.nativeElement.hidden = false;
      return;
    }
    if (
      !this.current ||
      !this.current[code] ||
      (!this.showCountriesOutsideRegion &&
        !this.getCodes().find((country) => country === code))
    ) {
      this.tooltip.nativeElement.hidden = true;
      return;
    }
    const totalPeople = this.getWorldData(code).headCount;

    let totalSpending = this.dataFilters.currency
      ? this.getWorldData(code).spending2021ppp
      : this.getWorldData(code).spendingCurrentUsd;

    this.mapTooltip.title = name.toUpperCase();
    this.mapTooltip.spending = this.dataFilters.currency
      ? this.current[code].spending2021ppp
      : this.current[code].spendingCurrentUsd;

    if (this.isCategories && this.dataFilters.category !== '0') {
      this.mapTooltip.spending = this.categoryService.getCategoryData(
        this.current[code]
      );
    }

    this.mapTooltip.people = this.current[code].headCount;
    this.mapTooltip.dailySpending = this.getAnnualSpendingPerCapita(
      this.mapTooltip.spending,
      this.mapTooltip.people
    );

    /*if (!this.dataFilters.spendingPer) {
      this.mapTooltip.spending /= Utils.getDaysInAYear(this.dataFilters.year);
      totalSpending /= Utils.getDaysInAYear(this.dataFilters.year);
    }*/

    this.mapTooltip.spendingCaption =
      (this.mapTooltip.spending / totalSpending) * 100;
    this.mapTooltip.spendingCaptionTotal = totalSpending;
    this.mapTooltip.peopleCaption =
      (this.mapTooltip.people / totalPeople) * 100;
    this.mapTooltip.peopleCaptionTotal = totalPeople;
    this.tooltip.nativeElement.hidden = false;
  }

  private calculateData(data: GroupedResponse<SimpleStatisticsResponse>) {
    if (!data) {
      return;
    }
    this.maxValue = Number.MIN_SAFE_INTEGER;
    this.minValue = Number.MAX_SAFE_INTEGER;
    this.mapData = Object.keys(data).reduce(
      (initial: { [key: string]: number }, countryCode: string) => {
        if (
          !this.showCountriesOutsideRegion &&
          !this.getCodes().find((c) => c === countryCode)
        ) {
          return initial;
        }

        let countryValue: number = 0;
        let numerator: number;
        let denominator: number;
        const denominatorData: SimpleStatisticsResponse =
          this.getWorldData(countryCode);

        if (this.mapHeaderData.title === 'annual_per_capita') {
          numerator = this.getAnnualSpendingPerCapita(
            this.dataFilters.currency
              ? data[countryCode].spending2021ppp
              : data[countryCode].spendingCurrentUsd,
            data[countryCode].headCount
          );
          denominator = 100;

          if (this.isCategories && this.dataFilters.category !== '0') {
            this.mapTooltip.spending = numerator =
              this.getAnnualSpendingPerCapita(
                this.categoryService.getCategoryData(data[countryCode]),
                data[countryCode].headCount
              );
            denominator = 100;
          }
        } else if (this.mapHeaderData.title === 'annual') {
          numerator = this.dataFilters.currency
            ? data[countryCode].spending2021ppp
            : data[countryCode].spendingCurrentUsd;
          denominator = this.dataFilters.currency
            ? denominatorData.spending2021ppp
            : denominatorData.spendingCurrentUsd;

          if (this.isCategories && this.dataFilters.category !== '0') {
            numerator = this.categoryService.getCategoryData(data[countryCode]);
            denominator = this.categoryService.getCategoryData(denominatorData);
          }
          if (!this.dataFilters.spendingPer) {
            numerator /= Utils.getDaysInAYear(this.dataFilters.year);
            denominator /= Utils.getDaysInAYear(this.dataFilters.year);
          }
        } else {
          // people
          numerator = data[countryCode].headCount;
          denominator = denominatorData.headCount;
        }

        if (this.mapHeaderData.type === 'share_of_country') {
          countryValue = (numerator / denominator) * 100;
        } else {
          countryValue = numerator;
        }
        if (countryValue > this.maxValue) {
          this.maxValue = countryValue;
        }
        if (countryValue < this.minValue) {
          this.minValue = countryValue;
        }

        return { ...initial, [countryCode]: countryValue };
      },
      {}
    );
  }

  private updateMap() {
    this.dataCacheByYearAndState &&
      this.handleData(this.dataCacheByYearAndState[this.dataFilters.year]);
    this.calculateData(this.current);

    this.worldMap?.changed();
    this.subnationalLayer?.changed();
    this.citiesLayer?.changed();
  }

  private styleCountriesLayer(resolution: number, f: Feature<Geometry>): Style {
    return new Style({
      fill:
        this.getCodes().length === 1
          ? new Fill({ color: 'transparent' })
          : undefined,
      stroke: new Stroke({
        color: Utils.getColorHex({
          name: ColorNames.Gray,
          shade: ColorShades.Shade400,
        }),
        width:
          0.2 + (this.map.getView().getZoomForResolution(resolution) || 0) - 2,
      }),
    });
  }

  private colorMapArea(
    resolution: number,
    f: Feature<Geometry>,
    selected: string[],
    showOutsideSelected: boolean = true
  ): Style | undefined {
    const areaCode = f.get('adm0_a3') || f.get('NUTS_ID') || f.get('CODE'); // either country code or subnational code
    let borderColor = [0, 0, 0];
    let borderWidth = 1;
    let zIndex = 0;

    if (selected.includes(areaCode)) {
      borderColor = Utils.getColorRgb({
        name: this.spendingGroupColor,
        shade: ColorShades.Shade700,
      });
      if (this.semiSelectedMode === areaCode) {
        borderWidth = 4;
      } else {
        borderWidth = 3;
      }

      borderWidth =
        borderWidth +
        (this.map.getView().getZoomForResolution(resolution) || 0) -
        2;
      zIndex = 1;
    }

    const color =
      (!showOutsideSelected && !selected.includes(areaCode)) ||
      (this.current && !this.current[areaCode]) ||
      !this.isCountryAllowed(areaCode)
        ? Utils.getColorHex({
            name: ColorNames.Gray,
            shade: ColorShades.Shade100,
          })
        : this.getCountryColor(f, this.mapData[areaCode]);
    f.set('COLOR', color);

    const style = new Style({
      fill: new Fill({ color }),
      stroke: new Stroke({
        color: `rgb(${borderColor[0]}, ${borderColor[1]}, ${borderColor[2]})`,
        width: borderWidth,
      }),
      zIndex,
    });

    return style;
  }

  private getCountryColor(
    feature: Feature<Geometry>,
    value: number | undefined
  ): string {
    if (value) {
      for (const [index, legendInterval] of this.legend.entries()) {
        if (
          value > legendInterval.min &&
          (legendInterval.max === undefined || value <= legendInterval.max)
        ) {
          const colorArray = this.legendColors.get(this.spendingGroupColor);
          if (colorArray && colorArray[index]) {
            const color = colorArray[index];
            const rgbColor = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
            feature.set('COLOR', rgbColor);
            return rgbColor;
          }
        }
      }
    }

    const defaultColorArray = this.legendColors.get(this.spendingGroupColor);
    if (defaultColorArray && defaultColorArray[0]) {
      const defaultColor = defaultColorArray[0];
      const rgbDefaultColor = `rgb(${defaultColor[0]}, ${defaultColor[1]}, ${defaultColor[2]})`;
      feature.set('COLOR', rgbDefaultColor);
      return rgbDefaultColor;
    }

    return Utils.getColorHex({
      name: this.spendingGroupColor,
      shade: ColorShades.Shade100,
    });
  }

  private handleData(d: GroupedResponse<SimpleStatisticsResponse> | undefined) {
    if (!d) {
      return;
    }

    this.current = d;

    if (!this.showCountriesOutsideRegion && !this.dataFilters.dataLens) {
      this.setWorldData(this.getCodes());
    } else if (this.dataFilters.dataLens) {
      this.setWorldData(Object.keys(this.current));
    } else {
      this.resetWorldData();
    }
  }

  private handleSelectionChanges() {
    const codes = this.getCodes();
    if (this.dataFilters.dataLens && codes.length === 1) {
      this.setSubnational(codes[0]);
    } else {
      this.removeSubnationalLayer();
    }

    if (codes.length === 0) {
      this.reset();
    } else if (codes.length === 1) {
      const extent = this.jsonValues.extent[codes[0]];
      if (extent) {
        this.map.getView().fit(extent, {
          duration: this.animationDuration,
          padding: [20, 20, 20, 20], // Adds padding around the zoom area
        });
      }
    } else {
      let ext: Extent = createEmpty();
      codes.forEach((c) => {
        let e = this.jsonValues.extent[c];
        if (e) {
          extend(ext, e);
        }
      });

      // Check if isEmpty
      if (ext[0] !== ext[2] && ext[1] !== ext[3]) {
        this.map.getView().fit(ext, { duration: this.animationDuration });
      }
    }
  }

  private setSubnational(countryCode: string) {
    if (!countryCode || this.subnationalLayer) {
      return;
    }

    let featureProj = 'EPSG:4326';
    if (countryCode === 'MEX') {
      // if mexico's subnational geojson data gets translated to EPSG:3857 this can be removed
      featureProj = 'EPSG:3857';
    }

    const vectorSource = new VectorSource({
      features: new GeoJSON().readFeatures(
        this.jsonValues.subnationalFeatures[countryCode],
        {
          dataProjection: 'EPSG:4326',
          featureProjection: featureProj,
        }
      ),
    });

    this.subnationalLayer = new VectorImageLayer({
      imageRatio: 2,
      source: vectorSource,
      zIndex: 2,
      style: (f, resolution) =>
        this.colorMapArea(resolution, f as Feature<Geometry>, this.getCodes()),
    });

    this.map.addLayer(this.subnationalLayer);
    return;
  }

  private removeSubnationalLayer() {
    if (this.subnationalLayer) {
      this.map.removeLayer(this.subnationalLayer);
      this.subnationalLayer = undefined;
    }
  }

  private isCountryAllowed(countryCode: string): boolean {
    return (
      this.authService.displayContinets() ||
      this.permissions.allowedCountries?.permissionExtended?.some(
        (country) => country === countryCode
      ) ||
      true
    );
  }

  private resetTooltip() {
    this.mapTooltip = {
      type: 'map',
      title: '',
      dailySpending: undefined,
      people: undefined,
      peopleCaption: undefined,
      spending: undefined,
      spendingCaption: undefined,
      showLock: false,
    };
  }
}
